import MDEditor from '@uiw/react-md-editor';
import rehypeSanitize from 'rehype-sanitize';
import { useTheme } from '../../contexts/ThemeContext';
import { POLICY_SANITIZE_SCHEMA } from '../../lib/markdownSanitize';
import './MarkdownDisplay.css';

interface MarkdownDisplayProps {
  content: string;
  className?: string;
  /** Render without the gray card container — flat markdown that inherits the
   *  surrounding text size (used by chat-style surfaces like Composer). */
  bare?: boolean;
}

/**
 * Renders markdown through `@uiw/react-md-editor`'s `MDEditor.Markdown`.
 *
 * `MDEditor.Markdown` runs `rehype-raw` ahead of any caller-supplied
 * `rehypePlugins` and ships no sanitizer of its own, so raw HTML in the source
 * would otherwise reach the DOM live — including on the unauthenticated
 * `/terms` and `/privacy` pages. `POLICY_SANITIZE_SCHEMA` is the shared
 * allow-list, kept byte-identical with Monarch Admin's `MarkdownPreview` so the
 * author's preview and the signed document are the same document.
 */
export function MarkdownDisplay({ content, className = '', bare = false }: MarkdownDisplayProps) {
  const { theme } = useTheme();

  if (!content) {
    return <div className={`text-gray-500 dark:text-gray-400 italic ${className}`}>No content</div>;
  }

  return (
    <div
      data-color-mode={theme === 'dark' ? 'dark' : 'light'}
      className={
        bare
          ? 'markdown-display-container'
          : 'bg-gray-50 dark:bg-gray-700 rounded-lg p-4 markdown-display-container'
      }
    >
      <div className={className}>
        <MDEditor.Markdown
          source={content}
          rehypePlugins={[[rehypeSanitize, POLICY_SANITIZE_SCHEMA]]}
          style={{
            backgroundColor: 'transparent',
            padding: 0,
            fontFamily: 'inherit',
            ...(bare ? { fontSize: 'inherit', color: 'inherit' } : {}),
          }}
          className="text-gray-900 dark:text-gray-100"
        />
      </div>
    </div>
  );
}
